/* eslint-disable no-restricted-syntax */
import { debounce, get } from 'lodash';
import React from 'react';
import { renderToString } from 'react-dom/server';
import { MiniLoader } from '@pwc/pwc-deals-ui';
import { Constants } from '@pwc/pwc-deals-web-common';

import { defaults as CKEditorDefaults } from 'common/components/RichTextEditor/CKEditorConfig';
import { syncedInfoWrapped } from 'common/components/Autosync/SyncedInfo';
import { setShowSharePointModalPowerPivot } from 'features/analyses/common/LibraryItemCard/PowerPivot/redux/powerPivotSlice';
import { isPowerPivotLibraryItem } from 'features/analyses/common/LibraryItemCard/PowerPivot/constants/PowerPivotTypes';
import {
  clearClipboard,
  deleteLibraryItem,
  getExcelLibraryItemsByGroup,
  getLibraryItemsFromClipboard,
  getPptLibraryItemsByGroup,
  toggleLibraryItemFormatForClipboard,
} from '../../features/contentLibrary/libraryItemService';
import { junctionColorMapping } from './junctionColorMapping';
import showAutosync from './ShowAutosync';
import { showModalError } from '../redux/errors/errorsSlice';
import { UNABLE_TO_ADD_OBJECT } from '../components/ModalError/constants';
import { isIngestionFormatTypeEnabled } from './featureFlags';

export const MINILOADER_LIBRARYITEMSTORE_ID = 'miniloader';

export const removeMiniloaderFromLibraryItemStore = () => {
  // eslint-disable-next-line no-underscore-dangle
  if (window.__LIBRARYITEMSTORE__.get(MINILOADER_LIBRARYITEMSTORE_ID)) {
    // eslint-disable-next-line no-underscore-dangle
    delete window.__LIBRARYITEMSTORE__._data.miniloader;
  }
};

async function fetchItemAndAutosync(projectId, request) {
  const item = await getExcelLibraryItemsByGroup(projectId, request);
  const autoSync = get(item, ['fileContents', 0, 'autoSync']);
  return { item, autoSync };
}

export function onSyncedInfoEventListener({
  canShowAutoSync,
  contextStatusInfoItem,
  data,
  editor,
  excelPPTItem,
  id,
  libraryItem,
  projectId,
  renderedHtml,
  renderedSvg,
  isPwcUser,
}) {
  return async (evt) => {
    if (
      evt?.target &&
      evt.target.id === `${id}-button` &&
      editor?.state !== 'destroyed'
    ) {
      let currentFormatType = data?.formatType;
      let autosync = '';
      try {
        // eslint-disable-next-line no-underscore-dangle
        const currentLibraryItem = window.__LIBRARYITEMSTORE__.get(id);
        if (
          Object.prototype.hasOwnProperty.call(
            currentLibraryItem,
            'currentFormatType'
          )
        ) {
          currentFormatType = currentLibraryItem.currentFormatType;
        }
        editor.execute('addLibraryItem', {
          id: MINILOADER_LIBRARYITEMSTORE_ID,
          rendered: renderToString(<MiniLoader />),
        });
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        await toggleLibraryItemFormatForClipboard(projectId, id, {
          formatType: currentFormatType === 'HTML' ? 'SVG' : 'HTML',
        });
        removeMiniloaderFromLibraryItemStore();

        if (canShowAutoSync) {
          autosync = syncedInfoWrapped({
            projectId,
            excelPPTItem: {
              ...excelPPTItem,
              formatType: currentFormatType === 'HTML' ? 'SVG' : 'HTML',
            },
            contextStatusInfoItem,
            isPwcUser,
          });
        }
        const contentToRender =
          currentFormatType === 'HTML' ? renderedSvg : renderedHtml;
        libraryItem.rendered = `${contentToRender}${autosync}`;
        editor.execute('addLibraryItem', libraryItem);
        const autoSyncInfo = {
          ...libraryItem.autoSync,
          data: {
            ...libraryItem?.autoSync?.data,
            formatType: currentFormatType === 'HTML' ? 'SVG' : 'HTML',
          },
        };
        // eslint-disable-next-line no-underscore-dangle
        window.__LIBRARYITEMSTORE__.add({
          ...libraryItem,
          autoSync: autoSyncInfo,
          data: {
            ...libraryItem?.data,
            formatType: currentFormatType === 'HTML' ? 'SVG' : 'HTML',
          },
          type: libraryItem.contentType,
          formatType: libraryItem.formatType,
          currentFormatType: currentFormatType === 'HTML' ? 'SVG' : 'HTML',
        });
      } catch (e) {
        editor.execute('addLibraryItem', {
          id: MINILOADER_LIBRARYITEMSTORE_ID,
          rendered: renderToString(<MiniLoader />),
        });
        // eslint-disable-next-line no-underscore-dangle
        const currentLibraryItem = window.__LIBRARYITEMSTORE__.get(id);
        autosync = syncedInfoWrapped({
          projectId,
          excelPPTItem: {
            ...excelPPTItem,
            formatType: currentFormatType,
          },
          contextStatusInfoItem,
          issueFound: true,
          isPwcUser,
        });
        const contentToRender =
          currentFormatType === 'HTML' ? renderedHtml : renderedSvg;
        libraryItem.rendered = `${contentToRender}${autosync}`;
        editor.execute('addLibraryItem', libraryItem);
        // eslint-disable-next-line no-underscore-dangle
        window.__LIBRARYITEMSTORE__._data[id] = {
          ...currentLibraryItem,
          currentFormatType,
        };
        removeMiniloaderFromLibraryItemStore();
        // eslint-disable-next-line no-console
        console.error('error', e);
      }
    }
  };
}

const noop = () => {};

function insertChartAndTable({
  contextStatusInfoItem,
  darkMode,
  dispatch,
  editor,
  editorContainer,
  isPwcUser,
  onChangeState = noop,
  projectId,
  setNoDataOnClipboard,
}) {
  async function insertMiniTable() {
    if (!editor) return;

    onChangeState({ type: 'start' });

    const stateLocal = {
      tasks: [],
    };

    let autosync = '';
    let excelPPTItem;
    editor.execute('addLibraryItem', {
      id: MINILOADER_LIBRARYITEMSTORE_ID,
      rendered: renderToString(<MiniLoader />),
    });

    try {
      const canShowAutosyncPromise = showAutosync(projectId, isPwcUser);
      const clipboard = await getLibraryItemsFromClipboard(projectId);

      if (!clipboard) {
        dispatch(setShowSharePointModalPowerPivot(false));
        setNoDataOnClipboard(true);
        if (editor) {
          editor.execute('undo');
          removeMiniloaderFromLibraryItemStore();
        }
        return;
      }

      stateLocal.tasks.push(clearClipboard(projectId, clipboard));
      const { id, type, data } = clipboard;

      if (
        // this is intentional, it's a boolean so effectively disabling this path
        isIngestionFormatTypeEnabled === 'enabled' &&
        type === Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value
      ) {
        const request = {
          ...data,
          groupedByKey: data.driveItemId,
          excelLibraryItems: Array.from({ length: 2 }).map(
            (_notUsed, index) => ({
              id,
              type,
              data: {
                formatType: index === 0 ? 'SVG' : 'HTML',
                worksheetName: data.worksheetName,
                uniqueId: data.uniqueId,
                cellRange: data.name,
              },
              childrenData: [],
            })
          ),
        };

        const { item, autoSync } = await fetchItemAndAutosync(
          projectId,
          request
        );

        const [{ value: contentSVG }, { value: contentHTML }] =
          item.fileContents;

        // set to SVG when fetching in the future
        stateLocal.tasks.push(
          toggleLibraryItemFormatForClipboard(projectId, id, {
            formatType: 'SVG',
          })
        );

        editor.execute(
          CKEditorDefaults.pluginMiniTableSVG.options.nameCommand,
          {
            payload: {
              ID: `MiniTableSVGToolbar-${id}`,
              onInsert() {
                editor.execute('registerLibraryItem', {
                  worksheetName: request.worksheetName,
                  groupName: request.groupName,
                  formatType: 'SVG',
                  libraryItemId: id,
                  sharePointItemUrl: request.sharePointItemUrl,
                  autoSync,
                });
              },
              onDelete() {
                deleteLibraryItem(projectId, id);
              },
              payload: {
                contents: {
                  image: contentSVG,
                  webTable: junctionColorMapping(contentHTML, darkMode, {
                    type,
                  }),
                },
                contentTypeInitial: 'image',
                onChangeValueRadio: debounce(({ current }) => {
                  toggleLibraryItemFormatForClipboard(projectId, id, {
                    formatType: current === 'image' ? 'SVG' : 'HTML',
                  });
                }, 250),
              },
            },
            type: 'createWidget',
          }
        );
      } else {
        let renderedHtml;
        let renderedSvg;
        let contentType;
        let request;

        if (
          type === Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value ||
          type === Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value
        ) {
          contentType = type;

          const rangeObject =
            type === Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value
              ? { namedRange: data.name, cellRange: data?.cellRange }
              : { cellRange: data.name };
          request = {
            ...data,
            groupedByKey: data.driveItemId,
            excelLibraryItems: [
              {
                id,
                type: contentType,
                data: {
                  formatType:
                    data?.formatType ||
                    process.env.REACT_APP_CELL_RANGE_FORMAT_TYPE,
                  worksheetName: data.worksheetName,
                  uniqueId: data.uniqueId,
                  ...rangeObject,
                },
                childrenData: [],
              },
              {
                id,
                type: contentType,
                data: {
                  formatType: 'SVG',
                  worksheetName: data.worksheetName,
                  uniqueId: data.uniqueId,
                  ...rangeObject,
                },
                childrenData: [],
              },
            ],
          };
          const { item, autoSync } = await fetchItemAndAutosync(
            projectId,
            request
          );
          const { worksheetName, groupName, sharePointItemUrl } = request;
          excelPPTItem = {
            worksheetName,
            groupName,
            formatType: data?.formatType,
            libraryItemId: id,
            sharePointItemUrl,
            autoSync,
          };
          renderedHtml = item.fileContents[0].value;
          renderedSvg = item.fileContents[1].value;
        } else if (type === Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value) {
          contentType = Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value;
          request = {
            ...data,
            groupedByKey: data.driveItemId,
            excelLibraryItems: [
              {
                id,
                type: contentType,
                data: {
                  formatType: 'SVG',
                  worksheetName: data.worksheetName,
                  uniqueId: data.uniqueId,
                  chartName: data.chartName,
                },
                childrenData: [],
              },
            ],
          };
          const { item, autoSync } = await fetchItemAndAutosync(
            projectId,
            request
          );
          const { worksheetName, groupName, sharePointItemUrl } = request;
          excelPPTItem = {
            worksheetName,
            groupName,
            formatType: data?.formatType,
            libraryItemId: id,
            sharePointItemUrl,
            autoSync,
          };

          renderedHtml = item.fileContents[0].value;
          const svgTransformed = junctionColorMapping(
            item.fileContents[0].value,
            false,
            null,
            true
          );
          renderedSvg = svgTransformed;
        } else if (
          type === Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value
        ) {
          contentType = Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value;
          request = {
            ...data,
            groupedByKey: data.driveItemId,
            pptLibraryItems: [
              {
                id,
                type: contentType,
                data: {
                  formatType: 'SVG',
                  groupName: data.groupName,
                  slideNumber: data.slideNumber,
                },
                childrenData: [],
              },
            ],
          };
          const item = await getPptLibraryItemsByGroup(projectId, request);
          const { worksheetName, groupName, sharePointItemUrl } = request;
          excelPPTItem = {
            worksheetName: `Slide ${data.slideNumber || data.SlideNumber} ${
              worksheetName || groupName
            }`,
            groupName,
            libraryItemId: id,
            sharePointItemUrl,
          };
          renderedHtml = item.fileContents[0].value;
          renderedSvg = null;
        } else if (isPowerPivotLibraryItem(type)) {
          // For error handling power pivot not supported - rich text kfa
          contentType = type;
        }

        const libraryItem = {
          id,
          // this property will be used to render content into CKEditor
          rendered: renderedHtml,
          renderedSvg,
          renderedHtml,
          contentType,
          request,
          type: contentType,
        };

        renderedHtml = junctionColorMapping(
          renderedHtml,
          darkMode,
          libraryItem
        );
        libraryItem.rendered = renderedHtml;
        const canShowAutosync = await canShowAutosyncPromise;

        if (canShowAutosync) {
          autosync = syncedInfoWrapped({
            projectId,
            excelPPTItem,
            contextStatusInfoItem,
            isPwcUser,
          });
        }

        libraryItem.rendered = isPowerPivotLibraryItem(type)
          ? ''
          : `${libraryItem.rendered}${autosync}`;

        // then make the API call to get the HTML data
        editor.execute('addLibraryItem', libraryItem);
        removeMiniloaderFromLibraryItemStore();
        const element = document.getElementById(id);

        element.addEventListener(
          'click',
          onSyncedInfoEventListener({
            canShowAutoSync: canShowAutosync,
            contextStatusInfoItem,
            data,
            editor,
            excelPPTItem,
            id,
            libraryItem,
            projectId,
            renderedHtml,
            renderedSvg,
            isPwcUser,
          })
        );

        if (isPowerPivotLibraryItem(type) && editor) {
          editor.execute('undo');
        }
      }

      await Promise.all(stateLocal.tasks);
      onChangeState({ type: 'success' });
    } catch (err) {
      if (editor) {
        editor.execute('undo');
        removeMiniloaderFromLibraryItemStore();
        dispatch(showModalError(UNABLE_TO_ADD_OBJECT));
      }
      onChangeState({ type: 'failure', payload: err });
    } finally {
      onChangeState({ type: 'end' });
    }
  }

  editorContainer.addEventListener(
    `inserting-minitable-chart`,
    insertMiniTable
  );

  return () => {
    editorContainer.removeEventListener(
      `inserting-minitable-chart`,
      insertMiniTable
    );
  };
}

export default insertChartAndTable;
